import React, { useState, useEffect, useCallback } from "react"
import {
  Communication,
  VALID_SECTIONS,
  ALWAYS_ENABLED_SECTIONS,
  ReportSection,
  useUpdateCommunication,
} from "~/api/communications"
import useDebouncedString from "@/hooks/useDebouncedString"

interface SectionsTabProps {
  communicationId: string
  communication?: Communication
}

const SectionsTab: React.FC<SectionsTabProps> = ({
  communicationId,
  communication,
}) => {
  const [selectedSections, setSelectedSections] = useState<string[]>([])
  const updateCommunicationMutation = useUpdateCommunication()

  const updateTone = useCallback(
    (tone: string) => {
      updateCommunicationMutation.mutate({
        id: communicationId,
        prompt_attribute_tone: tone,
      })
    },
    [communicationId, updateCommunicationMutation],
  )

  const updateGoals = useCallback(
    (goals: string) => {
      updateCommunicationMutation.mutate({
        id: communicationId,
        prompt_attribute_goals: goals,
      })
    },
    [communicationId, updateCommunicationMutation],
  )

  const updateGeneral = useCallback(
    (general: string) => {
      updateCommunicationMutation.mutate({
        id: communicationId,
        prompt_attribute_general: general,
      })
    },
    [communicationId, updateCommunicationMutation],
  )

  const updateHighLevelSummary = useCallback(
    (value: string) => {
      updateCommunicationMutation.mutate({
        id: communicationId,
        prompt_attribute_high_level_summary: value,
      })
    },
    [communicationId, updateCommunicationMutation],
  )

  const updateAttributionHighlights = useCallback(
    (value: string) => {
      updateCommunicationMutation.mutate({
        id: communicationId,
        prompt_attribute_attribution_highlights: value,
      })
    },
    [communicationId, updateCommunicationMutation],
  )

  const updateLiquidityEvents = useCallback(
    (value: string) => {
      updateCommunicationMutation.mutate({
        id: communicationId,
        prompt_attribute_liquidity_events: value,
      })
    },
    [communicationId, updateCommunicationMutation],
  )

  const updateNewInvestments = useCallback(
    (value: string) => {
      updateCommunicationMutation.mutate({
        id: communicationId,
        prompt_attribute_new_investments: value,
      })
    },
    [communicationId, updateCommunicationMutation],
  )

  const updateCashFlows = useCallback(
    (value: string) => {
      updateCommunicationMutation.mutate({
        id: communicationId,
        prompt_attribute_cash_flows: value,
      })
    },
    [communicationId, updateCommunicationMutation],
  )

  const { value: promptTone, setValue: setPromptTone } = useDebouncedString({
    initialValue: communication?.prompt_attribute_tone || "",
    onUpdate: updateTone,
    debounceMs: 500,
    shouldUpdate: !!communication,
  })

  const { value: promptGoals, setValue: setPromptGoals } = useDebouncedString({
    initialValue: communication?.prompt_attribute_goals || "",
    onUpdate: updateGoals,
    debounceMs: 500,
    shouldUpdate: !!communication,
  })

  const { value: promptGeneral, setValue: setPromptGeneral } =
    useDebouncedString({
      initialValue: communication?.prompt_attribute_general || "",
      onUpdate: updateGeneral,
      debounceMs: 500,
      shouldUpdate: !!communication,
    })

  const { value: promptHighLevelSummary, setValue: setPromptHighLevelSummary } =
    useDebouncedString({
      initialValue: communication?.prompt_attribute_high_level_summary || "",
      onUpdate: updateHighLevelSummary,
      debounceMs: 500,
      shouldUpdate: !!communication,
    })

  const {
    value: promptAttributionHighlights,
    setValue: setPromptAttributionHighlights,
  } = useDebouncedString({
    initialValue: communication?.prompt_attribute_attribution_highlights || "",
    onUpdate: updateAttributionHighlights,
    debounceMs: 500,
    shouldUpdate: !!communication,
  })

  const { value: promptLiquidityEvents, setValue: setPromptLiquidityEvents } =
    useDebouncedString({
      initialValue: communication?.prompt_attribute_liquidity_events || "",
      onUpdate: updateLiquidityEvents,
      debounceMs: 500,
      shouldUpdate: !!communication,
    })

  const { value: promptNewInvestments, setValue: setPromptNewInvestments } =
    useDebouncedString({
      initialValue: communication?.prompt_attribute_new_investments || "",
      onUpdate: updateNewInvestments,
      debounceMs: 500,
      shouldUpdate: !!communication,
    })

  const { value: promptCashFlows, setValue: setPromptCashFlows } =
    useDebouncedString({
      initialValue: communication?.prompt_attribute_cash_flows || "",
      onUpdate: updateCashFlows,
      debounceMs: 500,
      shouldUpdate: !!communication,
    })

  useEffect(() => {
    if (communication?.sections) {
      setSelectedSections(communication.sections)
    }
  }, [communication?.sections])

  const handleSectionToggle = (section: string) => {
    const newSelectedSections = selectedSections.includes(section)
      ? selectedSections.filter((s) => s !== section)
      : [...selectedSections, section]

    setSelectedSections(newSelectedSections)

    if (
      JSON.stringify(newSelectedSections.sort()) !==
      JSON.stringify((communication?.sections || []).sort())
    ) {
      updateCommunicationMutation.mutate({
        id: communicationId,
        sections: newSelectedSections,
      })
    }
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSections = e.target.checked ? [...VALID_SECTIONS] : []
    setSelectedSections(newSections)

    if (
      JSON.stringify(newSections.sort()) !==
      JSON.stringify((communication?.sections || []).sort())
    ) {
      updateCommunicationMutation.mutate({
        id: communicationId,
        sections: newSections,
      })
    }
  }

  const areAllSelected =
    VALID_SECTIONS.length > 0 &&
    VALID_SECTIONS.every((section) => selectedSections.includes(section))

  return (
    <div className="sections-tab">
      <h2 className="tab-heading">Report Sections</h2>
      <p className="tab-subtext">
        Select the sections you want to include in the report:
      </p>

      <div className="selected-count">
        {selectedSections.length} sections selected
      </div>

      <div className="table-container">
        <table className="sections-table">
          <thead>
            <tr>
              <th className="checkbox-column">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={areAllSelected}
                  onChange={handleSelectAll}
                />
              </th>
              <th>Select All</th>
            </tr>
          </thead>
          <tbody>
            {ALWAYS_ENABLED_SECTIONS.map((section) => (
              <tr key={section} className="selected">
                <td className="checkbox-column">
                  <input type="checkbox" checked disabled />
                </td>
                <td>{section}</td>
              </tr>
            ))}
            {VALID_SECTIONS.map((section) => (
              <tr
                key={section}
                className={selectedSections.includes(section) ? "selected" : ""}
              >
                <td className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={selectedSections.includes(section)}
                    onChange={() => handleSectionToggle(section)}
                  />
                </td>
                <td>{section}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="additional-settings">
        <h3 className="additional-settings-heading">
          Global Prompt Attributes
        </h3>
        <p className="tab-subtext">
          These attributes apply to all AI-generated content sections
        </p>

        <div className="additional-settings-section">
          <label htmlFor="promptTone">Tone</label>
          <textarea
            id="promptTone"
            value={promptTone}
            onChange={(e) => setPromptTone(e.target.value)}
            placeholder="e.g., Use a formal, institutional tone"
            rows={3}
          />
        </div>

        <div className="additional-settings-section">
          <label htmlFor="promptGoals">Goals</label>
          <textarea
            id="promptGoals"
            value={promptGoals}
            onChange={(e) => setPromptGoals(e.target.value)}
            placeholder="e.g., Focus on emphasizing growth and ROI"
            rows={3}
          />
        </div>

        <div className="additional-settings-section">
          <label htmlFor="promptGeneral">General</label>
          <textarea
            id="promptGeneral"
            value={promptGeneral}
            onChange={(e) => setPromptGeneral(e.target.value)}
            placeholder="e.g., Avoid technical jargon, write for non-financial audience"
            rows={3}
          />
        </div>
      </div>

      <div className="additional-settings">
        <h3 className="additional-settings-heading">
          Service-Specific Prompt Attributes
        </h3>
        <p className="tab-subtext">
          Customize prompts for individual AI services
        </p>

        <div className="additional-settings-section">
          <label htmlFor="promptHighLevelSummary">High Level Summary</label>
          <textarea
            id="promptHighLevelSummary"
            value={promptHighLevelSummary}
            onChange={(e) => setPromptHighLevelSummary(e.target.value)}
            placeholder="Additional guidance specific to high level summary generation..."
            rows={3}
          />
        </div>

        <div className="additional-settings-section">
          <label htmlFor="promptAttributionHighlights">
            Attribution Highlights
          </label>
          <textarea
            id="promptAttributionHighlights"
            value={promptAttributionHighlights}
            onChange={(e) => setPromptAttributionHighlights(e.target.value)}
            placeholder="Additional guidance specific to attribution highlights..."
            rows={3}
          />
        </div>

        <div className="additional-settings-section">
          <label htmlFor="promptLiquidityEvents">Liquidity Events</label>
          <textarea
            id="promptLiquidityEvents"
            value={promptLiquidityEvents}
            onChange={(e) => setPromptLiquidityEvents(e.target.value)}
            placeholder="Additional guidance specific to liquidity events..."
            rows={3}
          />
        </div>

        <div className="additional-settings-section">
          <label htmlFor="promptNewInvestments">New Investments</label>
          <textarea
            id="promptNewInvestments"
            value={promptNewInvestments}
            onChange={(e) => setPromptNewInvestments(e.target.value)}
            placeholder="Additional guidance specific to new investments..."
            rows={3}
          />
        </div>

        <div className="additional-settings-section">
          <label htmlFor="promptCashFlows">Cash Flows</label>
          <textarea
            id="promptCashFlows"
            value={promptCashFlows}
            onChange={(e) => setPromptCashFlows(e.target.value)}
            placeholder="Additional guidance specific to cash flows..."
            rows={3}
          />
        </div>
      </div>
    </div>
  )
}

export default SectionsTab
